import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import Icon1 from '../assets/TrefleTrans.svg';
import Icon2 from '../assets/CoeurTrans.svg';
import SingleImage from '../assets/terrain1.png';
import SingleImage2 from '../assets/terrain2.png';
import SingleImage3 from '../assets/terrain3.png';
import SingleImage4 from '../assets/terrain4.png';
import SingleImage5 from '../assets/terrain5.png';

gsap.registerPlugin(ScrollTrigger);

// ====== Angles fixes (une seule source de vérité) ======
// Ajuste ces valeurs à ton goût (en degrés, +/- 10° max pour rester lisible)
const CARD_ANGLES = [12, 3, -5, 6, -2];


// Pinning wrapper
const Section = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const PageContainer = styled.div`
  width: 100%;
  height: 120vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  margin-bottom: 5%;
  position: relative;
  text-align: center;
  background-color: rgba(102, 15, 19, 1);

  @media (max-width: 1024px) {
    height: 100vh;
    padding-bottom: 4rem;
  }
  @media (max-width: 800px) {
    height: 110vh;
    padding-bottom: 0rem;
    padding-left: 0rem;
    padding-right:0rem;
    overflow:hidden;
  }
`;

const Title = styled.p`
  font-size: 8rem;
  font-family: 'MongoGrotesqueLight';
  margin: 0;
  color: #fff;
  display: flex;
  align-items: center;

  @media (max-width: 900px) {
    display: none;
  }
`;

const TitleMob = styled.p`
  display: none;

  @media (max-width: 900px) {
    display: flex;
    font-size: 5rem;
    font-family: 'MongoGrotesqueLight';
    margin: 0;
    color: #fff;
    align-items: center;
    text-align: center;
  }
  @media (max-width: 410px) {
    font-size: 4rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  margin: 0;
  color: white;
`;

const TitreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 2%;
  width: 80%;
  text-align: center;
`;

const LargeIcon = styled.img`
  width: 250px;
  height: 250px;
  position: absolute;
  left: 0;
  top: 40%;

  @media (max-width: 900px) {
    display: none;
  }
`;

const LargeIcon2 = styled.img`
  width: 250px;
  height: 250px;
  position: absolute;
  right: 0;
  top: 40%;

  @media (max-width: 900px) {
    display: none;
  }
`;

// Zone pin + pile
const MaskContainer = styled.div`
  width: 100%;
  height: 100vh;
  height: calc(var(--vh) * 100);
  overflow: hidden;
  position: relative;
  @media (max-width: 900px) {
    height: calc(var(--vh) * 120);
  }
`;

const ImageStack = styled.div`
  display: grid;
  grid-template-areas: 'stack';
  place-items: center;
  margin-top: 4rem;
`;

const LayerWrapper = styled.div<{ offset: number }>`
  grid-area: stack;
  transform: translateX(${({ offset }) => offset}%);
  transform-origin: center center;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  will-change: transform;
`;

const LayerImage = styled.img`
  display: block;
  width: 310px;
  height: 430px;

  @media (max-width: 900px) {
    width: 250px;
    height: 370px;
  }
`;

const Terrain: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const maskRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 900 : false
  );

  const images = [SingleImage, SingleImage2, SingleImage3, SingleImage4, SingleImage5];

  // --vh pour mobile
  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
      ScrollTrigger.refresh();
    };
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    setVh();
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  // Mobile flag
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const sectionEl = sectionRef.current;
    const maskEl = maskRef.current;
    if (!sectionEl || !maskEl) return;
  
    const cards = gsap.utils.toArray<HTMLElement>('.layer');
    const maskH = maskEl.clientHeight;
    const maskW = maskEl.clientWidth;
  
    // === Config desktop vs mobile ===
    const DESKTOP = {
      TARGETS_X: [0.56, 0.61, 0.65, 0.71, 0.73], // un peu à droite, progressif
      D_IN: 0.45,
      D_SHIFT: 0.45,
      SCRUB: 0.3,
      SEGMENT_RATIO: 0.55,
      OVERLAP: 0.49,             // ~51% d’espace visible -> overlap modéré
      EXTRA_PER_RANK_RATIO: 0.02,
      LEFT_CLAMP_RATIO: 0.30,    // clamp à -30% du viewport
    };
  
    const MOBILE = {
      TARGETS_X: [0.52, 0.54, 0.56, 0.58, 0.60], // moins à droite pour rester safe
      D_IN: 0.40,
      D_SHIFT: 0.40,
      SCRUB: 0.25,
      SEGMENT_RATIO: 0.50,
      OVERLAP: 0.82,             // overlap fort -> peu d’espace entre cartes
      EXTRA_PER_RANK_RATIO: 0.01,// éventail très léger
      LEFT_CLAMP_RATIO: 0.18,    // clamp plus serré (évite de sortir à gauche)
    };
  
    const CFG = isMobile ? MOBILE : DESKTOP;
  
    // mesure largeur carte => espacement relatif stable
    const refCard = cards[0];
    const cardW = refCard ? refCard.clientWidth : 300;
  
    const STEP = cardW * (1 - CFG.OVERLAP);
    const EXTRA_PER_RANK = cardW * CFG.EXTRA_PER_RANK_RATIO;
    const LEFT_CLAMP = -maskW * CFG.LEFT_CLAMP_RATIO;
  
    // état initial : toutes les cartes sous la zone (arrivent par le bas)
    cards.forEach((card) => {
      const cardH = card.clientHeight;
      gsap.set(card, { y: maskH + cardH, x: 0, rotation: 0, zIndex: 1 });
    });
  
    // ScrollTrigger épinglé
    const startOffset = Math.round(window.innerHeight * (isMobile ? 0.3 : 0.15));
    const steps = cards.length;
    const segment = maskH * CFG.SEGMENT_RATIO;
    const endDistance = segment * steps;
  
    const tl = gsap.timeline({
      defaults: { ease: 'power2.out' },
      scrollTrigger: {
        trigger: sectionEl,
        start: () => `top+=${startOffset} top`,
        end: () => `+=${endDistance}`,
        scrub: CFG.SCRUB,
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        snap: {
          snapTo: (raw) => Math.round(raw * steps) / steps,
          duration: { min: 0.08, max: 0.16 },
          ease: 'power1.out',
        },
        // markers: true,
      },
    });
  
    // Séquence : arrivée par le bas, active à droite, précédentes rangées à gauche
    cards.forEach((card, k) => {
      const ratio = CFG.TARGETS_X[Math.min(k, CFG.TARGETS_X.length - 1)];
      const xActivePx = (ratio - 0.5) * maskW;
  
      // priorité z-index à la carte active
      tl.set(card, { zIndex: 100 + k }, k);
  
      // entrée de la carte active
      tl.to(
        card,
        {
          y: 0,
          x: xActivePx,
          rotation: CARD_ANGLES[k] ?? 0,
          duration: CFG.D_IN,
        },
        k
      );
  
      // ranger les précédentes avec espacement relatif (STEP) + légère ouverture
      for (let j = 0; j < k; j++) {
        const prev = cards[j];
        const rawX = xActivePx - (k - j) * STEP - j * EXTRA_PER_RANK;
        const clampedX = Math.max(rawX, LEFT_CLAMP);
  
        const baseRot = CARD_ANGLES[j] ?? 0;
        const jitter = (j % 2 ? 0.5 : -0.5); // très léger
  
        tl.to(
          prev,
          {
            x: clampedX,
            rotation: baseRot + jitter,
            duration: CFG.D_SHIFT,
            ease: 'power2.inOut',
            zIndex: 50 + j,
          },
          k
        );
      }
    });
  
    // Refresh si images chargent après
    ScrollTrigger.batch(maskEl.querySelectorAll('img'), {
      once: true,
      onEnter: () => ScrollTrigger.refresh(),
    });
  
    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
      tl.kill();
    };
  }, [isMobile]);

  return (
    <Section ref={sectionRef}>
      <PageContainer>
        <TitreContainer>
          <Title>NOS TERRAINS DE JEU</Title>
          <TitleMob>
            NOS TERRAINS<br />DE JEU
          </TitleMob>
          <Subtitle>Entre innovation, technologie et émotion.</Subtitle>
        </TitreContainer>

        <LargeIcon src={Icon1} alt="Icon 1" />
        <LargeIcon2 src={Icon2} alt="Icon 2" />

        <MaskContainer ref={maskRef}>
          <ImageStack>
            {images.map((img, i) => (
              <LayerWrapper key={i} className="layer" offset={0}>
                <LayerImage src={img} alt={`Layer ${i + 1}`} />
              </LayerWrapper>
            ))}
          </ImageStack>
        </MaskContainer>
      </PageContainer>
    </Section>
  );
};

export default Terrain;
